'use client';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { MediaCardArea, MediaCardData } from '@/components/ui/UiMediaCard';
import { UiMediaLightbox } from '@/components/ui/UiMediaLightbox';

type MediaLightboxContextValue = {
    open: (card: MediaCardData, area?: MediaCardArea) => void;
};

const MediaLightboxContext = createContext<MediaLightboxContextValue | null>(null);

/**
 * Null outside a provider — which is what lets UiMediaCard fall back to being
 * a plain tile there instead of a button that opens nothing.
 */
export function useMediaLightbox() {
    return useContext(MediaLightboxContext);
}

/**
 * One lightbox for the whole page, rather than one per tile.
 *
 * A Mediathek grid is thirty cards; thirty dialogs, thirty focus traps and
 * thirty <video> elements would be thirty times the cost of the one that is
 * ever actually open. So the tiles only say which card they are, and the state
 * lives here.
 *
 * `shown` outlives `isOpen` on purpose: the closing transition still needs
 * something to render, and dropping the card only afterwards is also what
 * unmounts the video — which is what stops the sound.
 */
export function MediaLightboxProvider({ children }: { children: ReactNode }) {
    const [shown, setShown] = useState<{ card: MediaCardData; area?: MediaCardArea } | null>(null);
    const [isOpen, setOpen] = useState(false);

    const open = useCallback((card: MediaCardData, area?: MediaCardArea) => {
        setShown({ card, area });
        setOpen(true);
    }, []);

    const value = useMemo(() => ({ open }), [open]);

    return (
        <MediaLightboxContext.Provider value={value}>
            {children}

            <UiMediaLightbox
                video={shown?.card.video}
                poster={shown?.card.image}
                title={shown?.card.title}
                area={shown?.area}
                open={isOpen}
                onClose={() => setOpen(false)}
                onClosed={() => setShown(null)}
            />
        </MediaLightboxContext.Provider>
    );
}
